import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Users, FileText, CreditCard, MessageSquare, BarChart3,
  ArrowLeft, Loader2, RefreshCw, Eye, TrendingUp, Layers
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fetchAdmin = async (path) => {
  const token = localStorage.getItem('session_token');
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 403) throw new Error('Not admin');
  if (!res.ok) throw new Error('Request failed');
  return res.json();
};

const updateAdmin = async (path, data) => {
  const token = localStorage.getItem('session_token');
  const res = await fetch(`${API}${path}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Update failed');
  return res.json();
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatCurrency = (paise) => {
  if (!paise && paise !== 0) return '₹0';
  return `₹${(paise / 100).toLocaleString('en-IN')}`;
};

const SCORE_COLORS = {
  Strong: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Viable: 'bg-blue-50 text-blue-700 border-blue-200',
  Fragile: 'bg-amber-50 text-amber-700 border-amber-200',
  'High Risk': 'bg-orange-50 text-orange-700 border-orange-200',
  'Not Investable': 'bg-red-50 text-red-700 border-red-200',
};

const STATUS_COLORS = {
  requested: 'bg-amber-50 text-amber-700 border-amber-200',
  paid: 'bg-blue-50 text-blue-700 border-blue-200',
  scheduled: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  created: 'bg-gray-50 text-gray-600 border-gray-200',
};

// ─── Stat Card ───
function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E5EA] p-4 md:p-6">
      <Icon className="h-4 w-4 text-[#86868B] mb-2" strokeWidth={1.5} />
      <div className="text-2xl md:text-3xl font-semibold text-[#1D1D1F]">{value}</div>
      <div className="text-xs text-[#86868B] mt-1">{label}</div>
      {sub && <div className="text-xs text-[#0071E3] mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Data Table (mobile-responsive) ───
function DataTable({ columns, data, onRowClick }) {
  if (!data || data.length === 0) {
    return <p className="text-sm text-[#86868B] text-center py-12">No data found.</p>;
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-table-desktop">
          <thead>
            <tr className="border-b border-[#E5E5EA]">
              {columns.map((col) => (
                <th key={col.key} className="text-left py-3 px-3 text-[#86868B] font-medium text-xs whitespace-nowrap">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr
                key={i}
                onClick={() => onRowClick?.(row)}
                className={`border-b border-[#F5F5F7] transition-colors hover:bg-[#FBFBFD] ${onRowClick ? 'cursor-pointer' : ''}`}
              >
                {columns.map((col) => (
                  <td key={col.key} className="py-3 px-3 text-[#1D1D1F] text-xs whitespace-nowrap max-w-[200px] truncate">
                    {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="admin-table-mobile">
        {data.map((row, i) => (
          <div
            key={i}
            onClick={() => onRowClick?.(row)}
            className={`bg-white rounded-2xl border border-[#E5E5EA] p-4 ${onRowClick ? 'cursor-pointer active:bg-[#F5F5F7]' : ''}`}
          >
            {columns.slice(0, 4).map((col) => (
              <div key={col.key} className="flex justify-between items-center py-1">
                <span className="text-xs text-[#86868B]">{col.label}</span>
                <span className="text-xs text-[#1D1D1F] font-medium max-w-[55%] text-right truncate">
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Main Admin Page ───
export default function Admin() {
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();
  const [authorized, setAuthorized] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [payments, setPayments] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [creditTxns, setCreditTxns] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [grievances, setGrievances] = useState([]);
  const [leads, setLeads] = useState([]);
  const [patterns, setPatterns] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);

  const checkAdmin = useCallback(async () => {
    try {
      await fetchAdmin('/admin/check');
      setAuthorized(true);
    } catch {
      setAuthorized(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { login(); return; }
    checkAdmin();
  }, [user, authLoading, checkAdmin, login]);

  const loadTab = useCallback(async (tab) => {
    setDataLoading(true);
    try {
      switch (tab) {
        case 'overview':
          setStats(await fetchAdmin('/admin/stats'));
          break;
        case 'users':
          setUsers(await fetchAdmin('/admin/users'));
          break;
        case 'reports':
          setReports(await fetchAdmin('/admin/reports'));
          break;
        case 'payments':
          setPayments(await fetchAdmin('/admin/payments'));
          break;
        case 'consultations':
          setConsultations(await fetchAdmin('/admin/consultations'));
          break;
        case 'scenarios':
          setScenarios(await fetchAdmin('/admin/scenarios'));
          break;
        case 'credits':
          setCreditTxns(await fetchAdmin('/admin/credit-transactions'));
          break;
        case 'feedback':
          setFeedback(await fetchAdmin('/admin/feedback'));
          break;
        case 'grievances':
          setGrievances(await fetchAdmin('/admin/grievances'));
          break;
        case 'leads':
          setLeads(await fetchAdmin('/admin/leads'));
          break;
        case 'patterns':
          setPatterns(await fetchAdmin('/admin/patterns'));
          break;
        default:
          break;
      }
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authorized) loadTab(activeTab);
  }, [authorized, activeTab, loadTab]);

  const handleTabChange = (t) => setActiveTab(t);

  const updateConsultationStatus = async (id, status) => {
    try {
      await updateAdmin(`/admin/consultations/${id}`, { status });
      toast.success(`Updated to ${status}`);
      loadTab('consultations');
    } catch {
      toast.error('Update failed');
    }
  };

  if (authLoading || authorized === null) {
    return (
      <div className="min-h-screen bg-[#FBFBFD] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#86868B]" />
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen bg-[#FBFBFD] flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <Users className="h-6 w-6 text-red-500" strokeWidth={1.5} />
          </div>
          <h2 className="text-xl font-medium text-[#1D1D1F] mb-2" data-testid="admin-denied">Access Denied</h2>
          <p className="text-sm text-[#86868B] mb-6">Admin access is restricted to authorized accounts only.</p>
          <Button onClick={() => navigate('/')} className="bg-[#1D1D1F] text-white hover:bg-[#434344] rounded-full px-6">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  // ─── Column Definitions ───
  const userCols = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'plan', label: 'Plan', render: (v) => <Badge variant="outline" className="text-xs rounded-full capitalize">{v || 'free'}</Badge> },
    { key: 'credits', label: 'Credits', render: (v) => v ?? 0 },
    { key: 'created_at', label: 'Joined', render: (v) => formatDate(v) },
  ];

  const reportCols = [
    { key: 'report_id', label: 'ID', render: (v) => <span className="font-mono text-[10px]">{v}</span> },
    { key: 'user_id', label: 'User', render: (v) => <span className="font-mono text-[10px]">{v?.slice(0, 16) || '—'}</span> },
    { key: 'model_label', label: 'Model' },
    { key: 'score', label: 'Score', render: (v) => (
      <div className="flex items-center gap-2">
        <span className="font-semibold">{v?.total ?? '—'}</span>
        {v?.label && <Badge className={`text-[10px] rounded-full border ${SCORE_COLORS[v.label] || ''}`}>{v.label}</Badge>}
      </div>
    )},
    { key: 'plan', label: 'Plan', render: (v) => <Badge variant="outline" className="text-xs rounded-full capitalize">{v || 'free'}</Badge> },
    { key: 'created_at', label: 'Created', render: (v) => formatDate(v) },
  ];

  const paymentCols = [
    { key: 'payment_id', label: 'ID', render: (v) => <span className="font-mono text-[10px]">{v}</span> },
    { key: 'user_id', label: 'User', render: (v) => <span className="font-mono text-[10px]">{v?.slice(0, 16) || '—'}</span> },
    { key: 'plan', label: 'Plan', render: (v) => <Badge variant="outline" className="text-xs rounded-full capitalize">{v?.replace('_', ' ')}</Badge> },
    { key: 'amount', label: 'Amount', render: (v) => <span className="font-semibold">{formatCurrency(v)}</span> },
    { key: 'status', label: 'Status', render: (v) => <Badge className={`text-[10px] rounded-full border ${STATUS_COLORS[v] || ''}`}>{v}</Badge> },
    { key: 'razorpay_payment_id', label: 'Razorpay ID', render: (v) => <span className="font-mono text-[10px]">{v || '—'}</span> },
    { key: 'created_at', label: 'Date', render: (v) => formatDate(v) },
  ];

  const consultationCols = [
    { key: 'consultation_id', label: 'ID', render: (v) => <span className="font-mono text-[10px]">{v}</span> },
    { key: 'user_id', label: 'User', render: (v) => <span className="font-mono text-[10px]">{v?.slice(0, 16) || '—'}</span> },
    { key: 'consultation_type', label: 'Type', render: (v) => <span className="capitalize">{v?.replace('_', ' ')}</span> },
    { key: 'status', label: 'Status', render: (v, row) => (
      <Select defaultValue={v} onValueChange={(newVal) => updateConsultationStatus(row.consultation_id, newVal)}>
        <SelectTrigger className="h-7 text-xs rounded-lg border-[#E5E5EA] w-[120px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {['requested', 'paid', 'scheduled', 'completed'].map((s) => (
            <SelectItem key={s} value={s} className="text-xs capitalize">{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    )},
    { key: 'preferred_date', label: 'Date', render: (v) => v || '—' },
    { key: 'notes', label: 'Notes', render: (v) => v ? <span className="max-w-[150px] truncate block">{v}</span> : '—' },
    { key: 'created_at', label: 'Requested', render: (v) => formatDate(v) },
  ];

  const scenarioCols = [
    { key: 'scenario_id', label: 'ID', render: (v) => <span className="font-mono text-[10px]">{v}</span> },
    { key: 'report_id', label: 'Report', render: (v) => <span className="font-mono text-[10px]">{v}</span> },
    { key: 'label', label: 'Label' },
    { key: 'score', label: 'Score', render: (v) => (
      <div className="flex items-center gap-2">
        <span className="font-semibold">{v?.total ?? '—'}</span>
        {v?.label && <Badge className={`text-[10px] rounded-full border ${SCORE_COLORS[v.label] || ''}`}>{v.label}</Badge>}
      </div>
    )},
    { key: 'created_at', label: 'Created', render: (v) => formatDate(v) },
  ];

  const creditCols = [
    { key: 'tx_id', label: 'TX ID', render: (v) => <span className="font-mono text-[10px]">{v}</span> },
    { key: 'user_id', label: 'User', render: (v) => <span className="font-mono text-[10px]">{v?.slice(0, 16) || '—'}</span> },
    { key: 'type', label: 'Type', render: (v) => <Badge variant="outline" className="text-xs rounded-full capitalize">{v?.replace('_', ' ')}</Badge> },
    { key: 'amount', label: 'Credits', render: (v) => <span className={`font-semibold ${v > 0 ? 'text-emerald-600' : 'text-red-500'}`}>{v > 0 ? `+${v}` : v}</span> },
    { key: 'plan', label: 'Plan', render: (v) => v || '—' },
    { key: 'created_at', label: 'Date', render: (v) => formatDate(v) },
  ];

  const tabs = [
    { value: 'overview', label: 'Overview', icon: BarChart3 },
    { value: 'users', label: 'Users', icon: Users },
    { value: 'reports', label: 'Reports', icon: FileText },
    { value: 'payments', label: 'Payments', icon: CreditCard },
    { value: 'consultations', label: 'Sessions', icon: MessageSquare },
    { value: 'scenarios', label: 'Scenarios', icon: Layers },
    { value: 'credits', label: 'Credits', icon: TrendingUp },
    { value: 'feedback', label: 'Feedback', icon: MessageSquare },
    { value: 'grievances', label: 'Grievances', icon: MessageSquare },
    { value: 'leads', label: 'Leads', icon: Users },
    { value: 'patterns', label: 'AI Insights', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-[#FBFBFD]" data-testid="admin-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#E5E5EA]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between h-14 md:h-16">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="text-[#86868B] hover:text-[#1D1D1F]">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <span className="text-[#1D1D1F] font-semibold text-sm md:text-base tracking-tight">Admin Panel</span>
          </div>
          <Button
            data-testid="admin-refresh-btn"
            onClick={() => loadTab(activeTab)}
            variant="ghost"
            size="icon"
            className="text-[#86868B] h-8 w-8"
          >
            <RefreshCw className={`h-4 w-4 ${dataLoading ? 'animate-spin' : ''}`} strokeWidth={1.5} />
          </Button>
        </div>
      </nav>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-10">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          {/* Scrollable tab bar for mobile */}
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 mb-6 md:mb-8">
            <TabsList className="bg-[#F5F5F7] rounded-full p-1 inline-flex min-w-max">
              {tabs.map(({ value, label, icon: Icon }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  data-testid={`admin-tab-${value}`}
                  className="rounded-full text-xs md:text-sm px-3 md:px-4 data-[state=active]:bg-white gap-1.5"
                >
                  <Icon className="h-3.5 w-3.5 hidden sm:block" strokeWidth={1.5} />
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Overview */}
          <TabsContent value="overview">
            {stats ? (
              <div className="space-y-8">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                  <StatCard icon={Users} label="Total Users" value={stats.total_users} />
                  <StatCard icon={FileText} label="Reports" value={stats.total_reports} />
                  <StatCard icon={CreditCard} label="Payments" value={stats.total_payments} />
                  <StatCard icon={MessageSquare} label="Consultations" value={stats.total_consultations} />
                  <StatCard icon={Layers} label="Scenarios" value={stats.total_scenarios} />
                  <StatCard icon={TrendingUp} label="Revenue" value={formatCurrency(stats.total_revenue)} />
                </div>
                {stats.plan_breakdown?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-[#E5E5EA] p-4 md:p-6">
                    <h3 className="text-sm font-medium text-[#1D1D1F] mb-4">Revenue by Plan</h3>
                    <div className="space-y-3">
                      {stats.plan_breakdown.map((p) => (
                        <div key={p._id} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="text-xs rounded-full capitalize">{p._id?.replace('_', ' ')}</Badge>
                            <span className="text-xs text-[#86868B]">{p.count} payment{p.count !== 1 ? 's' : ''}</span>
                          </div>
                          <span className="text-sm font-semibold text-[#1D1D1F]">{formatCurrency(p.revenue)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#86868B]" /></div>
            )}
          </TabsContent>

          {/* Users */}
          <TabsContent value="users">
            <div className="bg-white rounded-2xl border border-[#E5E5EA] p-3 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-[#1D1D1F]">All Users ({users.length})</h3>
              </div>
              <DataTable columns={userCols} data={users} />
            </div>
          </TabsContent>

          {/* Reports */}
          <TabsContent value="reports">
            <div className="bg-white rounded-2xl border border-[#E5E5EA] p-3 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-[#1D1D1F]">All Reports ({reports.length})</h3>
              </div>
              <DataTable
                columns={reportCols}
                data={reports}
                onRowClick={(row) => row.report_id && navigate(`/report/${row.report_id}`)}
              />
            </div>
          </TabsContent>

          {/* Payments */}
          <TabsContent value="payments">
            <div className="bg-white rounded-2xl border border-[#E5E5EA] p-3 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-[#1D1D1F]">All Payments ({payments.length})</h3>
              </div>
              <DataTable columns={paymentCols} data={payments} />
            </div>
          </TabsContent>

          {/* Consultations */}
          <TabsContent value="consultations">
            <div className="bg-white rounded-2xl border border-[#E5E5EA] p-3 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-[#1D1D1F]">All Consultations ({consultations.length})</h3>
              </div>
              <DataTable columns={consultationCols} data={consultations} />
            </div>
          </TabsContent>

          {/* Scenarios */}
          <TabsContent value="scenarios">
            <div className="bg-white rounded-2xl border border-[#E5E5EA] p-3 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-[#1D1D1F]">All Scenarios ({scenarios.length})</h3>
              </div>
              <DataTable columns={scenarioCols} data={scenarios} />
            </div>
          </TabsContent>

          {/* Credit Transactions */}
          <TabsContent value="credits">
            <div className="bg-white rounded-2xl border border-[#E5E5EA] p-3 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-[#1D1D1F]">Credit Transactions ({creditTxns.length})</h3>
              </div>
              <DataTable columns={creditCols} data={creditTxns} />
            </div>
          </TabsContent>

          <TabsContent value="feedback">
            <div className="bg-white rounded-2xl border border-[#E5E5EA] p-3 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-[#1D1D1F]">User Feedback ({feedback.length})</h3>
              </div>
              {feedback.length === 0 ? (
                <p className="text-sm text-[#86868B] text-center py-12">No feedback yet.</p>
              ) : (
                <div className="space-y-3">
                  {feedback.map((f, i) => (
                    <div key={i} className={`rounded-xl border p-4 ${f.rating === 'positive' ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-bold ${f.rating === 'positive' ? 'text-emerald-700' : 'text-red-700'}`}>
                          {f.rating === 'positive' ? 'Accurate' : 'Needs Improvement'}
                        </span>
                        <span className="text-[10px] text-[#86868B]">{formatDate(f.created_at)}</span>
                      </div>
                      <div className="text-xs text-[#1D1D1F]/60 space-y-0.5">
                        <p>{f.model_type} | {f.city} | {f.cuisine} | Score: {f.score_total} ({f.score_label})</p>
                        <p>AOV: ₹{f.aov} | Food Cost: {f.food_cost_pct}% | Margin: {f.operating_margin}%</p>
                        {f.comment && <p className="mt-2 text-[#1D1D1F] font-medium">"{f.comment}"</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="grievances">
            <div className="bg-white rounded-2xl border border-[#E5E5EA] p-3 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-[#1D1D1F]">Grievances / Issue Reports ({grievances.length})</h3>
              </div>
              {grievances.length === 0 ? (
                <p className="text-sm text-[#86868B] text-center py-12">No grievances reported.</p>
              ) : (
                <div className="space-y-3">
                  {grievances.map((g, i) => (
                    <div key={i} className={`rounded-xl border p-4 ${g.status === 'open' ? 'border-amber-200 bg-amber-50/30' : g.status === 'resolved' ? 'border-emerald-200 bg-emerald-50/30' : 'border-[#E5E5EA]'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-[#1D1D1F] capitalize">{g.issue_type?.replace('_', ' ')}</span>
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${g.status === 'open' ? 'bg-amber-200 text-amber-800' : g.status === 'resolved' ? 'bg-emerald-200 text-emerald-800' : 'bg-gray-200 text-gray-700'}`}>{g.status}</span>
                        </div>
                        <span className="text-[10px] text-[#86868B]">{formatDate(g.created_at)}</span>
                      </div>
                      <p className="text-xs text-[#1D1D1F]/70 mb-2">{g.description}</p>
                      <div className="flex items-center gap-3 text-[10px] text-[#86868B]">
                        <span>{g.user_name} ({g.user_email})</span>
                        {g.report_id && <span>Report: {g.report_id}</span>}
                        {g.photo_path && <span className="text-[#0071E3] font-medium">Has photo</span>}
                      </div>
                      {g.photo_path && (
                        <div className="mt-2">
                          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/grievances/photo/${g.photo_path}?token=${localStorage.getItem('session_token')}`} target="_blank" rel="noreferrer"
                            className="text-[10px] text-[#0071E3] font-semibold hover:underline">View Photo</a>
                        </div>
                      )}
                      <div className="mt-3 flex gap-2">
                        {['open', 'reviewing', 'resolved'].map(s => (
                          <button key={s} onClick={async () => { await updateAdmin(`/admin/grievances/${g.grievance_id}`, { status: s }); loadTab('grievances'); toast.success(`Updated to ${s}`); }}
                            className={`text-[9px] font-semibold px-2.5 py-1 rounded-full border ${g.status === s ? 'bg-[#1D1D1F] text-white border-[#1D1D1F]' : 'border-[#E5E5EA] text-[#1D1D1F]/60 hover:bg-[#F5F5F7]'}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="leads">
            <div className="bg-white rounded-2xl border border-[#E5E5EA] p-3 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-[#1D1D1F]">Leads / Contact Details ({leads.length})</h3>
              </div>
              {leads.length === 0 ? (
                <p className="text-sm text-[#86868B] text-center py-12">No leads captured yet.</p>
              ) : (
                <DataTable columns={[
                  { key: 'contact_name', label: 'Name' },
                  { key: 'contact_phone', label: 'Phone', render: (v) => <a href={`tel:${v}`} className="text-[#0071E3] hover:underline">{v}</a> },
                  { key: 'contact_email', label: 'Email', render: (v) => <a href={`mailto:${v}`} className="text-[#0071E3] hover:underline">{v}</a> },
                  { key: 'contact_location', label: 'Location' },
                  { key: 'google_email', label: 'Google', render: (v) => <span className="text-[10px] text-[#86868B]">{v}</span> },
                  { key: 'city', label: 'Business City' },
                  { key: 'preferred_model', label: 'Model', render: (v) => v ? <span className="capitalize text-xs">{v?.replace('_', ' ')}</span> : '—' },
                  { key: 'last_score', label: 'Score', render: (v) => <span className="font-bold">{v || '—'}</span> },
                  { key: 'updated_at', label: 'Last Active', render: (v) => formatDate(v) },
                ]} data={leads} />
              )}
            </div>
          </TabsContent>

          <TabsContent value="patterns">
            <div className="bg-white rounded-2xl border border-[#E5E5EA] p-3 md:p-6">
              <h3 className="text-sm font-medium text-[#1D1D1F] mb-4">AI Learning Insights</h3>
              {!patterns || patterns.total_reports === 0 ? (
                <p className="text-sm text-[#86868B] text-center py-12">Not enough data yet. Need at least 3 reports.</p>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard icon={FileText} label="Total Reports" value={patterns.total_reports} />
                    <StatCard icon={BarChart3} label="Avg Score" value={`${patterns.avg_score}/100`} />
                    <StatCard icon={TrendingUp} label="Avg Margin" value={`${patterns.avg_margin}%`} />
                    <StatCard icon={CreditCard} label="Avg Food Cost" value={`${patterns.avg_food_cost}%`} />
                  </div>

                  <div>
                    <h4 className="text-xs font-bold text-[#1D1D1F] mb-2">Score Distribution</h4>
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(patterns.label_distribution || {}).map(([label, count]) => (
                        <span key={label} className="text-xs px-3 py-1 rounded-full bg-[#F5F5F7] border border-[#E5E5EA]">{label}: {count}</span>
                      ))}
                    </div>
                  </div>

                  {patterns.model_stats?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-[#1D1D1F] mb-2">Performance by Model</h4>
                      <div className="space-y-2">
                        {patterns.model_stats.map((m, i) => (
                          <div key={i} className="flex items-center justify-between text-xs bg-[#F5F5F7] rounded-lg px-3 py-2">
                            <span className="font-medium text-[#1D1D1F] capitalize">{m.model?.replace('_', ' ')}</span>
                            <span className="text-[#86868B]">{m.count} reports | Avg score: {m.avg_score}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {patterns.city_stats?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-[#1D1D1F] mb-2">Performance by City</h4>
                      <div className="space-y-2">
                        {patterns.city_stats.map((c, i) => (
                          <div key={i} className="flex items-center justify-between text-xs bg-[#F5F5F7] rounded-lg px-3 py-2">
                            <span className="font-medium text-[#1D1D1F]">{c.city}</span>
                            <span className="text-[#86868B]">{c.count} reports | Avg score: {c.avg_score}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {patterns.top_risks?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-[#1D1D1F] mb-2">Most Common Risks</h4>
                      <div className="space-y-2">
                        {patterns.top_risks.map((r, i) => (
                          <div key={i} className="flex items-center justify-between text-xs bg-[#F5F5F7] rounded-lg px-3 py-2">
                            <span className="font-medium text-[#1D1D1F]">{r.flag}</span>
                            <span className="text-[#86868B]">{r.count}x | {r.severity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {patterns.feedback?.total > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-[#1D1D1F] mb-2">Feedback Summary</h4>
                      <p className="text-xs text-[#86868B]">
                        {patterns.feedback.positive} positive / {patterns.feedback.negative} negative out of {patterns.feedback.total} total
                      </p>
                    </div>
                  )}

                  <p className="text-[10px] text-[#1D1D1F]/30">This data is fed into the AI analysis for every new report, making insights more contextual over time.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
